import { api, type ApiError } from "./client";
import { useResource } from "./useResource";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

// Seats and compute credits (specifications/billing.md). Shapes mirror api.md
// §Billing; money is a JSON number in USD.

// Stripe's subscription statuses, passed through as Stripe reports them.
export type SubscriptionStatus =
  | "active"
  | "canceled"
  | "incomplete_expired"
  | "incomplete"
  | "past_due"
  | "paused"
  | "trialing"
  | "unpaid";

// The reason a `402 payment_required` would carry.
export type BlockedReason = "no_subscription" | "out_of_credits";

// The server stores what Stripe last reported, which can lack a period or a
// quantity — hence the nulls.
export type ApiBillingSubscription = {
  readonly cancel_at_period_end: boolean;
  readonly current_period_end: string | null;
  readonly seats: number | null;
  readonly status: SubscriptionStatus;
};

export type ApiAutoTopUp = {
  readonly amount_usd: number;
  readonly enabled: boolean;
  // Set when a decline stopped it; saving the settings clears it.
  readonly failed_at: string | null;
  readonly last_error: string | null;
  readonly threshold_usd: number;
};

export type ApiBillingCredits = {
  readonly auto_top_up: ApiAutoTopUp;
  readonly balance_usd: number;
  // "Visa •••• 4242"; null until a checkout or top-up has saved a card.
  readonly payment_method: string | null;
};

export type ApiBillingAccount = {
  readonly blocked_reason: BlockedReason | null;
  readonly credits: ApiBillingCredits;
  readonly enabled: true;
  // Live seat count; the subscription's own `seats` catches up on the next
  // billing tick.
  readonly seats: number;
  readonly subscription: ApiBillingSubscription | null;
  readonly top_up_limits: {
    readonly max_usd: number;
    readonly min_usd: number;
  };
};

// A deployment without Stripe configured answers only this.
export type ApiBilling = { readonly enabled: false } | ApiBillingAccount;

// `billing.updated` carries no summary, so both reads refetch on it — and on a
// resync, which stands for whatever was missed while disconnected.
const useRefetchOnBillingUpdate = (
  workspaceId: string | null,
  refetch: () => void,
): void => {
  useRealtimeScope(workspaceId ? `workspace:${workspaceId}` : null, (event) => {
    if (event.kind === "billing.updated" || event.kind === "resync") {
      refetch();
    }
  });
};

export type UseBilling = {
  readonly billing: ApiBilling | null;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useBilling = (workspaceId: string | null): UseBilling => {
  const { data, error, loading, refetch } = useResource<ApiBilling>(
    workspaceId ? `/workspaces/${workspaceId}/billing` : null,
  );
  useRefetchOnBillingUpdate(workspaceId, refetch);
  return { billing: data ?? null, error, loading, refetch };
};

export type LedgerKind = "auto_top_up" | "top_up" | "usage";

export type ApiLedgerEntry = {
  // Negative for usage, positive for a top-up.
  readonly amount_usd: number;
  readonly created_at: string;
  readonly kind: LedgerKind;
  readonly public_id: string;
  // Usage only: the UTC day the row accumulates, and its cloud runner-hours.
  readonly usage_date: string | null;
  readonly usage_hours: number | null;
};

export type UseBillingLedger = {
  readonly entries: ReadonlyArray<ApiLedgerEntry>;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
};

// Settings shows the latest few, not the server's default page of 50.
const LEDGER_LIMIT = 10;

/** Newest first. Admin-only on the server, so `enabled` gates the request. */
export const useBillingLedger = (
  workspaceId: string | null,
  enabled: boolean,
): UseBillingLedger => {
  const scoped = enabled ? workspaceId : null;
  const { data, error, loading, refetch } = useResource<{
    entries: ReadonlyArray<ApiLedgerEntry>;
  }>(scoped ? `/workspaces/${scoped}/billing/ledger` : null, {
    limit: LEDGER_LIMIT,
  });
  useRefetchOnBillingUpdate(scoped, refetch);
  return { entries: data?.entries ?? [], error, loading };
};

// Each of these answers a Stripe-hosted page to send the browser to. Checkout
// returns to `/settings?billing=subscribed|topped-up|cancelled`, the portal to
// `/settings`.
export type StripeRedirect = { readonly url: string };

export const startCheckout = (workspaceId: string): Promise<StripeRedirect> =>
  api.post(`/workspaces/${workspaceId}/billing/checkout`);

export const openBillingPortal = (
  workspaceId: string,
): Promise<StripeRedirect> =>
  api.post(`/workspaces/${workspaceId}/billing/portal`);

/** `amountUsd` is a whole number within `top_up_limits`. */
export const startTopUp = (
  workspaceId: string,
  amountUsd: number,
): Promise<StripeRedirect> =>
  api.post(`/workspaces/${workspaceId}/billing/top-ups`, {
    amount_usd: amountUsd,
  });

export type AutoTopUpSettings = {
  readonly amount_usd: number;
  readonly enabled: boolean;
  readonly threshold_usd: number;
};

// Sensitive: a stale session is challenged with `re_auth_required`, which the
// client re-verifies and replays like any other call.
export const updateAutoTopUp = (
  workspaceId: string,
  settings: AutoTopUpSettings,
): Promise<ApiBilling> =>
  api.put(`/workspaces/${workspaceId}/billing/auto-top-up`, settings);
